import type { TFunction } from "i18next";
import type {
  ChannelMetadata,
  TelemetryDataPoint,
  TelemetryNote,
} from "@/types";
import { getChannelColor, type ChannelColorIndices } from "./channelColors";
import {
  CHART_CONSTANTS,
  groupNotesByX,
  noteIconFont,
  type ChartMargins,
  type LapTiming,
} from "./chartLayout";

const LAP_COLOR = "#1e2035";

export interface ChannelStats {
  min: number;
  max: number;
  mean: number;
}

// Pure canvas renderer primitives and per-layer draw functions shared by the
// CanvasChart drawing pipeline. Each renderer draws one layer of the chart;
// CanvasChart orchestrates them in order. Call sequences must stay stable —
// see __tests__/CanvasChart.draw.test.tsx.

export const setupHiDpiCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
) => {
  // Assigning the bitmap size resets the canvas, so no explicit clear is
  // needed before drawing.
  const devicePixelRatio = window.devicePixelRatio || 1;
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.scale(devicePixelRatio, devicePixelRatio);
};

const drawText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  fontSize = 12,
  align: "left" | "center" | "right" = "left",
  bold = false,
) => {
  ctx.fillStyle = color;
  ctx.font = `${bold ? "bold " : ""}${fontSize}px ${CHART_CONSTANTS.FONT_FAMILY}`;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y);
};

const drawLine = (
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  width = 1,
  dash: number[] = [],
) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.setLineDash([]);
};

export interface DrawGridParams {
  showGrid: boolean;
  xDomain: [number, number];
  xScale: (timestamp: number) => number;
  selectedChannels: string[];
  channelDomains: Record<string, [number, number]>;
  yScales: Record<string, (value: number) => number>;
  margins: ChartMargins;
  width: number;
  height: number;
}

export const drawGrid = (
  ctx: CanvasRenderingContext2D,
  {
    showGrid,
    xDomain,
    xScale,
    selectedChannels,
    channelDomains,
    yScales,
    margins,
    width,
    height,
  }: DrawGridParams,
) => {
  if (!showGrid) return;

  // Vertical grid lines (time)
  const timeStep = (xDomain[1] - xDomain[0]) / 10;
  for (let i = 0; i <= 10; i++) {
    const timestamp = xDomain[0] + i * timeStep;
    const x = xScale(timestamp);
    drawLine(
      ctx,
      x,
      margins.top,
      x,
      height - margins.bottom,
      "rgba(255,255,255,0.04)",
      1,
      [3, 3],
    );
  }

  // Horizontal grid lines for each channel
  selectedChannels.forEach((channel) => {
    const domain = channelDomains[channel];
    const yScale = yScales[channel];
    if (!domain || !yScale) return;

    const step = (domain[1] - domain[0]) / 5;
    for (let i = 0; i <= 5; i++) {
      const value = domain[0] + i * step;
      const y = yScale(value);
      drawLine(
        ctx,
        margins.left,
        y,
        width - margins.right,
        y,
        "rgba(255,255,255,0.04)",
        1,
        [3, 3],
      );
    }
  });
};

export interface DrawLapBarParams {
  lapData?: LapTiming[];
  lapBarHeight: number;
  xScale: (timestamp: number) => number;
  t: TFunction;
}

export const drawLapBar = (
  ctx: CanvasRenderingContext2D,
  { lapData, lapBarHeight, xScale, t }: DrawLapBarParams,
) => {
  if (!lapData || lapData.length === 0) return;

  const lapBarTop = 30;
  const lapBarBottom = lapBarTop + lapBarHeight - 10;

  lapData.forEach((lap) => {
    if (lap.startTime == null || lap.endTime == null) return; // skip undrawable laps
    const lapColor = LAP_COLOR;
    const startX = xScale(lap.startTime);
    const endX = xScale(lap.endTime);

    // Draw lap background
    ctx.fillStyle = lapColor;
    ctx.fillRect(startX, lapBarTop, endX - startX, lapBarBottom - lapBarTop);

    // Draw lap border
    drawLine(ctx, startX, lapBarTop, startX, lapBarBottom, "#2a2a3a", 1);
    drawLine(ctx, endX, lapBarTop, endX, lapBarBottom, "#2a2a3a", 1);
    drawLine(ctx, startX, lapBarTop, endX, lapBarTop, "#2a2a3a", 1);
    drawLine(ctx, startX, lapBarBottom, endX, lapBarBottom, "#2a2a3a", 1);

    // Draw lap number text
    const lapCenterX = (startX + endX) / 2;
    const lapCenterY = (lapBarTop + lapBarBottom) / 2;
    drawText(
      ctx,
      t("pages:sessions.telemetry.chart.canvasLapLabel", { number: lap.lapNumber }),
      lapCenterX,
      lapCenterY,
      "#6b7280",
      12,
      "center",
      true,
    );
  });
};

export interface DrawChannelsParams {
  data: TelemetryDataPoint[];
  viewportData: TelemetryDataPoint[];
  selectedChannels: string[];
  channelColorIndices?: ChannelColorIndices;
  channelMetadata: Record<string, ChannelMetadata>;
  channelDomains: Record<string, [number, number]>;
  channelStats: Record<string, ChannelStats>;
  yScales: Record<string, (value: number) => number>;
  xScale: (timestamp: number) => number;
  margins: ChartMargins;
  chartHeight: number;
  chartAreaHeight: number;
  width: number;
  t: TFunction;
}

export const drawChannels = (
  ctx: CanvasRenderingContext2D,
  {
    data,
    viewportData,
    selectedChannels,
    channelColorIndices,
    channelMetadata,
    channelDomains,
    channelStats,
    yScales,
    xScale,
    margins,
    chartHeight,
    chartAreaHeight,
    width,
    t,
  }: DrawChannelsParams,
) => {
  selectedChannels.forEach((channel, index) => {
    const color = getChannelColor(channel, channelColorIndices, index);
    const domain = channelDomains[channel];
    const yScale = yScales[channel];
    const unit = channelMetadata[channel]?.unit || "";

    if (!domain || !yScale) return;

    const chartTop = index * chartHeight + margins.top;
    const chartBottom = chartTop + chartAreaHeight;

    // Draw Y-axis
    drawLine(ctx, margins.left, chartTop, margins.left, chartBottom, "#2a2a3a", 1);

    // Draw Y-axis ticks and labels
    const formatValue = (value: number) => {
      if (unit === "rpm" || unit === "#") {
        return value.toFixed(0);
      }
      return value.toFixed(1);
    };

    for (let i = 0; i <= 5; i++) {
      const value = domain[0] + (i * (domain[1] - domain[0])) / 5;
      const y = yScale(value);

      // Tick mark
      drawLine(ctx, margins.left - 5, y, margins.left, y, "#2a2a3a", 1);

      // Label
      drawText(ctx, formatValue(value), margins.left - 8, y, "#4b5563", 11, "right");
    }

    // Channel label
    drawText(
      ctx,
      `${channel} (${unit})`,
      margins.left + 8,
      chartTop + 20,
      color,
      14,
      "left",
      true,
    );

    // Stats (min/max/avg)
    const stats = channelStats[channel];
    if (stats) {
      const statsText = t("pages:sessions.telemetry.chart.canvasStatsMinMaxAvg", {
        min: formatValue(stats.min),
        max: formatValue(stats.max),
        avg: formatValue(stats.mean),
      });
      drawText(
        ctx,
        statsText,
        width - margins.right - 10,
        chartTop + 20,
        "#4b5563",
        11,
        "right",
      );
    }

    // Draw data line
    ctx.strokeStyle = color;
    ctx.lineWidth = 0.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();

    let firstPoint = true;
    data.forEach((point) => {
      const value = point.values[channel];
      if (value === undefined) return;

      const x = xScale(point.timestamp);
      const y = yScale(value);

      if (firstPoint) {
        ctx.moveTo(x, y);
        firstPoint = false;
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Calculate and draw local min/max markers within viewport
    if (viewportData.length > 0) {
      const channelValues = viewportData
        .map((point, idx) => ({
          timestamp: point.timestamp,
          value: point.values[channel],
          index: idx,
        }))
        .filter((item) => item.value !== undefined);

      if (channelValues.length > 0) {
        // Find local min and max in a single pass
        const { localMin, localMax } = channelValues.reduce(
          (acc, current) => {
            if (current.value! < acc.localMin.value!) {
              acc.localMin = current;
            }
            if (current.value! > acc.localMax.value!) {
              acc.localMax = current;
            }
            return acc;
          },
          { localMin: channelValues[0], localMax: channelValues[0] },
        );

        // Draw min marker
        if (localMin.value !== undefined) {
          const minX = xScale(localMin.timestamp);
          const minY = yScale(localMin.value);
          drawMinMaxMarker(
            ctx,
            minX,
            minY,
            localMin.value,
            true,
            formatValue,
          );
        }

        // Draw max marker
        if (localMax.value !== undefined) {
          const maxX = xScale(localMax.timestamp);
          const maxY = yScale(localMax.value);
          drawMinMaxMarker(
            ctx,
            maxX,
            maxY,
            localMax.value,
            false,
            formatValue,
          );
        }
      }
    }
  });
};

export interface DrawXAxisParams {
  xDomain: [number, number];
  xScale: (timestamp: number) => number;
  margins: ChartMargins;
  width: number;
  height: number;
}

export const drawXAxis = (
  ctx: CanvasRenderingContext2D,
  { xDomain, xScale, margins, width, height }: DrawXAxisParams,
) => {
  // Draw X-axis (bottom)
  drawLine(
    ctx,
    margins.left,
    height - margins.bottom,
    width - margins.right,
    height - margins.bottom,
    "#2a2a3a",
    1,
  );

  // X-axis ticks and labels
  const timeStep = (xDomain[1] - xDomain[0]) / 10;
  for (let i = 0; i <= 10; i++) {
    const timestamp = xDomain[0] + i * timeStep;
    const x = xScale(timestamp);

    // Tick mark
    drawLine(
      ctx,
      x,
      height - margins.bottom,
      x,
      height - margins.bottom + 5,
      "#2a2a3a",
      1,
    );

    // Label
    drawText(
      ctx,
      `${timestamp.toFixed(1)}s`,
      x,
      height - margins.bottom + 20,
      "#4b5563",
      12,
      "center",
    );
  }
};

export interface DrawSyncLineParams {
  syncCurrentTime?: number;
  xDomain: [number, number];
  xScale: (timestamp: number) => number;
  margins: ChartMargins;
  height: number;
  t: TFunction;
}

export const drawSyncLine = (
  ctx: CanvasRenderingContext2D,
  {
    syncCurrentTime,
    xDomain,
    xScale,
    margins,
    height,
    t,
  }: DrawSyncLineParams,
) => {
  // Keep the positive form: a NaN sync time must be skipped, and
  // `NaN < a || NaN > b` is false where `!(NaN >= a && NaN <= b)` is true.
  const inDomain =
    syncCurrentTime !== undefined &&
    syncCurrentTime >= xDomain[0] &&
    syncCurrentTime <= xDomain[1];
  if (!inDomain) {
    return;
  }

  const syncX = xScale(syncCurrentTime);
  drawLine(
    ctx,
    syncX,
    margins.top,
    syncX,
    height - margins.bottom,
    "#3b82f6", // blue color for sync line
    1,
    [5, 5],
  );

  // Add sync time label
  drawText(
    ctx,
    t("pages:sessions.telemetry.chart.canvasVideoTime", { time: syncCurrentTime.toFixed(1) }),
    syncX + 5,
    margins.top + 15,
    "#3b82f6",
    11,
    "left",
    true,
  );
};

export interface DrawNoteMarkersParams {
  notes: TelemetryNote[];
  currentTimeDomain: [number, number];
  draggingNoteId: string | null;
  noteDragStart: { mouseX: number } | null;
  selectedNoteId?: string | null;
  hoveredNoteId: string | null;
  xScale: (timestamp: number) => number;
  margins: ChartMargins;
  width: number;
  height: number;
}

export const drawNoteMarkers = (
  ctx: CanvasRenderingContext2D,
  {
    notes,
    currentTimeDomain,
    draggingNoteId,
    noteDragStart,
    selectedNoteId,
    hoveredNoteId,
    xScale,
    margins,
    width,
    height,
  }: DrawNoteMarkersParams,
) => {
  if (notes.length === 0) return;

  // Filter notes that are within the visible time range
  // Always include notes that are currently being dragged
  const visibleNotes = notes.filter(
    (note) =>
      (note.timeOffset >= currentTimeDomain[0] &&
        note.timeOffset <= currentTimeDomain[1]) ||
      draggingNoteId === note.id,
  );

  // Group notes that are very close together to prevent overlap
  const groupedNotes = groupNotesByX(
    visibleNotes,
    (note) => {
      // If this note is being dragged, use mouse position directly for x coordinate
      if (draggingNoteId === note.id && noteDragStart) {
        // Constrain dragged position to chart bounds
        return Math.max(
          margins.left,
          Math.min(width - margins.right, noteDragStart.mouseX),
        );
      }
      return xScale(note.timeOffset);
    },
    (note) => draggingNoteId === note.id && !!noteDragStart,
  );

  // Draw each group of notes
  groupedNotes.forEach((group) => {
    const isMultiple = group.notes.length > 1;
    const isSelected =
      selectedNoteId && group.notes.some((note) => note.id === selectedNoteId);
    const isHovered =
      hoveredNoteId && group.notes.some((note) => note.id === hoveredNoteId);
    const isDragging =
      draggingNoteId && group.notes.some((note) => note.id === draggingNoteId);

    // Determine colors based on state
    let noteColor, fillColor, lineWidth, radius;
    if (isDragging) {
      noteColor = "#dc2626"; // red-600 - distinct color for dragging
      fillColor = "#3f0f0f"; // dark red fill for dark canvas
      lineWidth = 3;
      radius = 10;
    } else if (isSelected) {
      noteColor = "#9333ea"; // purple-600
      fillColor = "#9333ea";
      lineWidth = 3;
      radius = 9;
    } else if (isHovered) {
      noteColor = "#7c3aed"; // purple-700
      fillColor = "#1a0d2e"; // dark purple fill for dark canvas
      lineWidth = 3;
      radius = 9;
    } else {
      noteColor = "#a855f7"; // purple-500
      fillColor = "#1a1a2e";
      lineWidth = 2;
      radius = 8;
    }

    // Draw vertical line indicator with hover effect
    drawLine(
      ctx,
      group.x,
      margins.top,
      group.x,
      height - margins.bottom,
      noteColor,
      isDragging || isHovered || isSelected ? 3 : 2,
      [4, 4], // dashed line
    );

    // Draw note marker circle at the top
    const markerY = margins.top - 12;

    // Add subtle glow effect when hovered or dragging
    if ((isHovered && !isSelected) || isDragging) {
      ctx.shadowColor = noteColor;
      ctx.shadowBlur = isDragging ? 12 : 8; // Stronger glow when dragging
    }

    // Fill circle
    ctx.fillStyle = fillColor;
    ctx.beginPath();
    ctx.arc(group.x, markerY, radius, 0, 2 * Math.PI);
    ctx.fill();

    // Reset shadow
    ctx.shadowBlur = 0;

    // Border circle
    ctx.strokeStyle = noteColor;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(group.x, markerY, radius, 0, 2 * Math.PI);
    ctx.stroke();

    // Draw message icon (simplified)
    ctx.fillStyle = noteColor;
    ctx.font = noteIconFont(isDragging || isHovered || isSelected ? 11 : 10);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("💬", group.x, markerY);

    // Draw count badge for multiple notes
    if (isMultiple) {
      const badgeX = group.x + (radius + 2);
      const badgeY = markerY - (radius + 2);
      const badgeRadius = isHovered || isSelected ? 9 : 8;

      // Badge background
      ctx.fillStyle = "#dc2626"; // red-600
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeRadius, 0, 2 * Math.PI);
      ctx.fill();

      // Badge text
      ctx.fillStyle = "#ffffff";
      ctx.font = noteIconFont(isHovered || isSelected ? 11 : 10);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(group.notes.length.toString(), badgeX, badgeY);
    }
  });
};

// Helper function to draw min/max markers
const drawMinMaxMarker = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  value: number,
  isMin: boolean,
  formatValue: (value: number) => string,
) => {
  const color = isMin
    ? CHART_CONSTANTS.COLORS.MIN_MARKER
    : CHART_CONSTANTS.COLORS.MAX_MARKER;
  const arrow = isMin ? "↓" : "↑";
  const labelY = isMin
    ? y + CHART_CONSTANTS.MARKER.LABEL_OFFSET
    : y - CHART_CONSTANTS.MARKER.LABEL_OFFSET;
  const textBaseline = isMin ? "top" : "bottom";

  // Draw circle
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, CHART_CONSTANTS.MARKER.RADIUS, 0, 2 * Math.PI);
  ctx.fill();

  // Draw white border
  ctx.strokeStyle = CHART_CONSTANTS.COLORS.WHITE_BORDER;
  ctx.lineWidth = CHART_CONSTANTS.MARKER.BORDER_WIDTH;
  ctx.stroke();

  // Draw value label
  ctx.fillStyle = color;
  ctx.font = CHART_CONSTANTS.FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = textBaseline;
  ctx.fillText(`${arrow} ${formatValue(value)}`, x, labelY);
};
